import { isValidUUID } from "./uuid";

/**
 * Template-ID detection helpers
 *
 * These utilities tell us whether an ID found inside a Canvas template is still a
 * placeholder (“template” ID) or a real, production-style identifier generated
 * by the corresponding product.
 *
 * Real-world formats (confirmed via product repositories + production data):
 *   - Zap (Editor) ........... numeric string e.g. 309512564
 *   - Table (Tables) ......... ULID e.g. 01JWY01A3RRCNKMS2BF0CJWJ3H
 *   - Interface Project ...... CUID e.g. cmelwe9x2000emredhdor3jr9 (25 chars)
 *                              or 26-char variant e.g. clnuxybeu411890ol929f0yad4
 *   - Interface Page ......... CUID e.g. cmelwear8000imred1no8rbhu (25 chars)
 *                              or 26-char variant e.g. clnuxybgn412020ol9tlq72gbr
 *   - Chatbot ................ CUID, 25 or 26 chars (same family)
 *   - Agent (Copilot) ........ UUID v4 e.g. 09ee73ff-5630-46b0-9ddc-c4ce4277545f
 *
 * The functions below return `true` when the supplied ID DOES NOT match the real
 * format, meaning it is still a template placeholder.
 */

// purely numeric (one or more digits)
const NUMERIC_RE = /^\d+$/;

// ULID (26 Crockford-Base32 chars; case-insensitive)
const ULID_RE = /^[0-9A-HJKMNP-TV-Z]{26}$/i;

// CUID. The original CUID v1 format is "c" + 24 lowercase alphanumeric chars
// (25 chars total). Newer generators in production also produce a 26-char
// variant (e.g., Interface page id `clnuxybgn412020ol9tlq72gbr`). Both shapes
// are real production identifiers and must not be flagged as templates.
const CUID_RE = /^c[a-z0-9]{24,25}$/;

// legacy template markers appear as _TOKEN_ inside template IDs
const hasMarker = (s: string, token: string) =>
  s.toUpperCase().includes(`_${token.toUpperCase()}_`);

export function isTemplateZapId(zapId: string = "") {
  return hasMarker(zapId, "ZAP") || !NUMERIC_RE.test(zapId);
}

export function isTemplateTableId(tableId: string = "") {
  return hasMarker(tableId, "TABLE") || !ULID_RE.test(tableId);
}

export function isTemplateInterfaceProjectId(interfaceProjectId: string = "") {
  return (
    hasMarker(interfaceProjectId, "INTERFACE") ||
    !CUID_RE.test(interfaceProjectId)
  );
}

export function isTemplateInterfacePageId(interfacePageId: string = "") {
  return hasMarker(interfacePageId, "PAGE") || !CUID_RE.test(interfacePageId);
}

export function isTemplateChatbotId(chatbotId: string = "") {
  return hasMarker(chatbotId, "CHATBOT") || !CUID_RE.test(chatbotId);
}

export function isTemplateAgentId(agentId: string = "") {
  return hasMarker(agentId, "AGENT") || !isValidUUID(agentId);
}
